"use client"
import FirstPageIcon from "@mui/icons-material/FirstPage"
import LastPageIcon from "@mui/icons-material/LastPage"
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import {
  Link,
  Pagination as MuiPagination,
  PaginationProps as MuiPaginationProps,
  PaginationItem,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Button } from "../Button"

export type PaginationProps = MuiPaginationProps & {
  /** Compact mode shows fewer page numbers - ideal for narrow containers */
  compact?: boolean
  /** Show text labels (Prev/Next) even in compact mode */
  showLabels?: boolean
}

export const Pagination = (props: PaginationProps) => {
  const {
    count,
    page,
    onChange,
    compact = false,
    showLabels = false,
    siblingCount,
    boundaryCount,
    ...rest
  } = props
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const effectiveSiblingCount = siblingCount ?? (compact ? 0 : 1)
  const effectiveBoundaryCount = boundaryCount ?? 1

  const shouldShowLabels = showLabels || (!compact && !smDown)

  return (
    <MuiPagination
      {...rest}
      sx={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "nowrap",
        "& .MuiPagination-ul": {
          flexWrap: "nowrap",
        },
      }}
      count={count}
      page={page}
      onChange={onChange}
      siblingCount={effectiveSiblingCount}
      boundaryCount={effectiveBoundaryCount}
      hideNextButton={count && count === 1 ? true : false}
      hidePrevButton={count && count <= 1 ? true : false}
      renderItem={(item) => {
        return (
          <PaginationItem
            {...item}
            slots={{
              first: () => (
                <Button
                  component={Link}
                  ariaLabel="nav-button-first"
                  startIcon={<FirstPageIcon />}
                  color="secondary"
                  disableRipple
                  disableTouchRipple
                  sx={{ minWidth: "auto", px: compact ? 0.5 : 1 }}
                />
              ),
              previous: () => (
                <Button
                  component={Link}
                  ariaLabel="nav-button-prev"
                  startIcon={<NavigateBeforeIcon />}
                  color="secondary"
                  disableRipple
                  disableTouchRipple
                  sx={{ minWidth: "auto", px: compact ? 0.5 : 1 }}
                >
                  {shouldShowLabels ? "Prev" : ""}
                </Button>
              ),
              next: () => (
                <Button
                  component={Link}
                  ariaLabel="nav-button-next"
                  endIcon={<NavigateNextIcon />}
                  color="secondary"
                  disableRipple
                  disableTouchRipple
                  sx={{ minWidth: "auto", px: compact ? 0.5 : 1 }}
                >
                  {shouldShowLabels ? "Next" : ""}
                </Button>
              ),
              last: () => (
                <Button
                  component={Link}
                  ariaLabel="nav-button-last"
                  endIcon={<LastPageIcon />}
                  color="secondary"
                  disableRipple
                  disableTouchRipple
                  sx={{ minWidth: "auto", px: compact ? 0.5 : 1 }}
                />
              ),
            }}
            size={compact ? "small" : "large"}
            sx={{
              "&.Mui-selected": {
                background: "none",
              },
              borderRadius: 0,
              borderBottom: compact ? "3px solid" : "4px solid",
              borderColor: item.selected ? "primary.main" : "transparent",
              minWidth: compact ? 28 : 32,
            }}
          />
        )
      }}
    />
  )
}
